import { useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import { Box, Button, Stack, Typography } from "@mui/material";
import { ExportDataButton } from "components/ExportDataButton";
import { FacilityPicker } from "components/FacilityPicker";
import { FacilityReadOnlyBanner } from "components/FacilityReadOnlyBanner";
import {
  SAA_ACUTE_MASS_LIMIT_KG,
  SAA_NON_ACUTE_VOLUME_LIMIT_GAL,
} from "encamp-shared/src/constants/wasteAccumulation";
import {
  FacilityAccumulationAreasQuery,
  FacilityPickerFragment,
  VolumeStatus,
  WasteAccumulationAreaType,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useFacilityReadOnly } from "hooks/useFacilityReadOnly";
import { useTenant } from "hooks/useTenant";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EmptySectionState, SectionCard } from "routes/Customer/Waste/Containers/containerSections";

import { FieldBox } from "../components/FieldBox";
import { SectionFilters } from "../components/SectionFilters";
import { StatCardGrid } from "../components/StatCardGrid";
import { WastePageFrame } from "../components/WastePageFrame";
import { AccumulationAreaDialog } from "./AccumulationAreaDialog";
import { FACILITY_ACCUMULATION_AREAS_QUERY } from "./api";
import { AreaCard, AreaCardArea } from "./AreaCard";
import { RulesetPopover } from "./RulesetPopover";

type QueryFacility = NonNullable<FacilityAccumulationAreasQuery["facility"]>;

/** The section note under "Satellite accumulation", built from the federal
 * limits rather than restating them, so the copy can't drift from the numbers
 * the gauges are actually judged against. */
const SATELLITE_LIMITS_NOTE =
  `Threshold limits are ${SAA_NON_ACUTE_VOLUME_LIMIT_GAL} gal haz waste, ` +
  `and/or 1 qt liquid acute haz, or ${SAA_ACUTE_MASS_LIMIT_KG} kg solid acute haz`;

/** Central accumulation is time-governed, and the allowance depends on the
 * facility's generator status, so both are stated rather than picking one. */
const CENTRAL_LIMITS_NOTES = [
  "SQG RCRA Haz Threshold Limit: 180 days max on site per container standard, or 270 days max on " +
    "site per container if shipping more than 200 miles to receiving facility",
  "LQG RCRA Haz Threshold Limit: 90 days max on site per container",
];

/** "705 Boulder Dr, Breinigsville, PA 18031" from the facility's address parts,
 * skipping any that are unset rather than leaving stray commas behind. The zip
 * follows its state on a space, not a comma — comma-joining every part reads as
 * "PA, 18031". */
function formatFacilityAddress(facility: QueryFacility): string {
  const street = [facility.streetAddress1, facility.streetAddress2].filter(Boolean).join(" ");
  const cityState = [facility.city, facility.state].filter(Boolean).join(", ");
  const region = [cityState, facility.zip].filter(Boolean).join(" ");
  return [street, region].filter(Boolean).join(", ");
}

/** Matches an area against a section's search box: a plain substring test over
 * the two fields the card actually shows. */
function matchesSearch(area: AreaCardArea, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return [area.name, area.description].some((field) =>
    (field ?? "").toLowerCase().includes(needle)
  );
}

/**
 * Page title and facility identity, above the canvas.
 *
 * The facility name is the title, so the identity line carries only what the
 * title does not: the EPA ID and the address. Read-only, and with no facility
 * picker — the page is reached from a single waste facility, so switching
 * belongs to the breadcrumb.
 */
function PageHeader({
  facility,
  tenantId,
  facilityId,
  onAddArea,
  addDisabled,
}: {
  facility: QueryFacility | null | undefined;
  tenantId: string;
  facilityId: string;
  onAddArea: () => void;
  addDisabled: boolean;
}) {
  const primaryEpaId = facility?.epaIdStatuses?.find(
    (status) => status.isPrimaryForFacility
  )?.epaId;
  const identity = [
    primaryEpaId ? `EPA ID: ${primaryEpaId}` : null,
    facility ? formatFacilityAddress(facility) : null,
  ].filter(Boolean);

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      flexWrap="wrap"
      gap={2}
    >
      <Box>
        <Typography component="h1" variant="h5" sx={{ m: 0 }}>
          {facility?.name ? `${facility.name} Accumulation Areas` : "Accumulation Areas"}
        </Typography>
        {identity.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {identity.join(" · ")}
          </Typography>
        )}
      </Box>
      <Stack direction="row" spacing={1}>
        <ExportDataButton
          tenantId={tenantId}
          facilityId={facilityId}
          exportType="accumulationAreas"
          filter={{}}
          fileName="accumulation-areas"
        />
        {/* Contained: this is the page's only creation action, and an outlined
            primary sits indistinguishable next to the outlined Export. */}
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={onAddArea}
          disabled={addDisabled}
        >
          Add area
        </Button>
      </Stack>
    </Stack>
  );
}

type AreaCounts = {
  total: number;
  satellite: number;
  central: number;
  atThreshold: number;
  containersOnSite: number;
};

/** The on-site counts. Header and the five figures only — the page actions
 * live in the page header and the filters on the sections they filter. */
function OnSiteWasteCard({ counts }: { counts: AreaCounts }) {
  return (
    <SectionCard surface="paper" title="On-site waste">
      <StatCardGrid minWidth="9rem" testId="on-site-stats">
        <FieldBox label="Active Accumulation Areas" value={counts.total} emphasis="figure" />
        <FieldBox label="Satellite" value={counts.satellite} emphasis="figure" />
        <FieldBox label="Central" value={counts.central} emphasis="figure" />
        {/* Labelled "Areas", not the mockup's "Containers": this counts areas
            whose volume has tripped, which is what the data behind it says and
            what the cards below highlight. */}
        <FieldBox
          label="Areas at threshold"
          value={counts.atThreshold}
          emphasis="figure"
          valueColor={counts.atThreshold > 0 ? "error.main" : "text.primary"}
        />
        <FieldBox
          label="Total containers on-site"
          value={counts.containersOnSite}
          emphasis="figure"
        />
      </StatCardGrid>
    </SectionCard>
  );
}

/**
 * A titled list of area cards with its own search and over-threshold toggle on
 * the header line, plus the regulatory notes that govern the section.
 *
 * Filtering is per section rather than page-wide: each section's controls sit on
 * its own card, so a control that also emptied the other section would be
 * reaching outside the box it appears in.
 */
function AreaSection({
  label,
  notes,
  areas,
  searchLabel,
  areaType,
  facilityId,
}: {
  label: string;
  notes: string[];
  areas: AreaCardArea[];
  searchLabel: string;
  /** Which ruleset the header's popover should explain. */
  areaType: WasteAccumulationAreaType;
  facilityId: string;
}) {
  const [search, setSearch] = useState("");
  const [overThresholdOnly, setOverThresholdOnly] = useState(false);

  // Satellite only. A central area's volume has no cap, so its `volume.status`
  // resolves to OK for every one of them and the filter could only ever empty
  // the section. Central is governed on the time axis instead — filtering it by
  // nearest deadline is the equivalent control, once the warning horizon is
  // settled (the design draws 30 days in one place and 60 in another).
  const filterByThreshold = areaType === WasteAccumulationAreaType.Satellite;

  const visible = areas.filter(
    (area) =>
      matchesSearch(area, search) &&
      (!filterByThreshold || !overThresholdOnly || area.volume.status === VolumeStatus.Over)
  );

  return (
    <SectionCard
      surface="paper"
      title={label}
      // Beside the title, because it explains what the title means.
      adornment={<RulesetPopover facilityId={facilityId} areaType={areaType} />}
      // The card's right-edge slot. These were in `adornment`, which sits
      // inside the same stack as the title — a `flexGrow` there only grows
      // within that stack, so the filters ended up next to the heading instead
      // of at the card's edge.
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          <SectionFilters
            search={search}
            onSearchChange={setSearch}
            overThreshold={
              filterByThreshold
                ? { only: overThresholdOnly, onChange: setOverThresholdOnly }
                : undefined
            }
            searchLabel={searchLabel}
          />
        </Stack>
      }
    >
      <Stack spacing={1.5}>
        <Box>
          {notes.map((note) => (
            <Typography key={note} variant="body2" color="text.secondary">
              {note}
            </Typography>
          ))}
        </Box>
        {visible.length === 0 ? (
          <EmptySectionState message="No areas in this section match your search or filter." />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(20.625rem, 1fr))",
              gap: 2,
            }}
          >
            {visible.map((area) => (
              <AreaCard key={area.id} area={area} />
            ))}
          </Box>
        )}
      </Stack>
    </SectionCard>
  );
}

function FacilityAccumulationAreas({
  tenantId,
  facilityId,
}: {
  tenantId: string;
  facilityId: string;
}) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const { isReadOnly } = useFacilityReadOnly(facilityId);

  const { data, loading } = useQuery(FACILITY_ACCUMULATION_AREAS_QUERY, {
    variables: { facilityId },
    // This page remounts on the way back from an area's detail route (e.g.
    // after archiving), so cache-first would otherwise serve the stale
    // cached list until something else triggers a refetch.
    fetchPolicy: "cache-and-network",
  });

  const facility = data?.facility;
  const items: AreaCardArea[] = facility?.accumulationAreas?.items ?? [];
  const facilityName = facility?.name ?? "Facility";

  const satelliteAreas = items.filter((area) => area.type === WasteAccumulationAreaType.Satellite);
  const centralAreas = items.filter((area) => area.type === WasteAccumulationAreaType.Central);
  const counts: AreaCounts = {
    total: items.length,
    satellite: satelliteAreas.length,
    central: centralAreas.length,
    atThreshold: items.filter((area) => area.volume.status === VolumeStatus.Over).length,
    containersOnSite: items.reduce((sum, area) => sum + area.containerCount, 0),
  };

  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Facilities", to: `/o/${tenantId}/waste/facilities` },
    { label: facilityName, to: `/o/${tenantId}/waste/facilities/${facilityId}` },
    { label: "Accumulation Areas" },
  ]);

  return (
    <>
      <FacilityReadOnlyBanner facilityId={facilityId} />
      <WastePageFrame
        header={
          <PageHeader
            facility={facility}
            tenantId={tenantId}
            facilityId={facilityId}
            onAddArea={() => setAddDialogOpen(true)}
            addDisabled={isReadOnly}
          />
        }
      >
        <Stack spacing={2}>
          <OnSiteWasteCard counts={counts} />

          {!loading && items.length === 0 && (
            <EmptySectionState message="No accumulation areas are set up for this facility yet. Use “Add area” above to create the first one." />
          )}

          {satelliteAreas.length > 0 && (
            <AreaSection
              label="Satellite accumulation"
              notes={[SATELLITE_LIMITS_NOTE]}
              areas={satelliteAreas}
              searchLabel="Search satellite accumulation areas"
              areaType={WasteAccumulationAreaType.Satellite}
              facilityId={facilityId}
            />
          )}

          {centralAreas.length > 0 && (
            <AreaSection
              label="Central accumulation"
              notes={CENTRAL_LIMITS_NOTES}
              areas={centralAreas}
              searchLabel="Search central accumulation areas"
              areaType={WasteAccumulationAreaType.Central}
              facilityId={facilityId}
            />
          )}
        </Stack>
      </WastePageFrame>

      {addDialogOpen && (
        <AccumulationAreaDialog
          open
          facilityId={facilityId}
          onClose={() => setAddDialogOpen(false)}
        />
      )}
    </>
  );
}

/**
 * Shown when the page is reached without a `?facilityId=` — a cold-loaded
 * bookmark or a hand-typed URL. Redirecting those to the facilities list
 * silently dumped a working deep link somewhere else; picking here keeps the
 * reader on the page they asked for. The episodic events page keeps its own
 * copy rather than sharing, since both are a handful of lines that differ
 * only in their prompt text.
 */
function FacilityPickerPrompt({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // The facility view registers its own facility-named trail; this branch
  // returns before that component mounts, so without a crumb of its own the
  // picker sits on an otherwise bare page.
  useBreadcrumb([{ label: "Waste", to: `/o/${tenantId}/waste` }, { label: "Accumulation Areas" }]);

  const handleChange = (facility: FacilityPickerFragment | null) => {
    if (!facility) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("facilityId", facility.id);
    navigate({ search: next.toString() }, { replace: true });
  };

  return (
    <Stack spacing={2} sx={{ px: 3, py: 4, maxWidth: 420 }}>
      <Typography variant="body1">Select a facility to view its accumulation areas.</Typography>
      <FacilityPicker
        label="Facility"
        facilityQueryVariant="waste"
        value={null}
        onChange={handleChange}
        invalid={false}
        isTouched={false}
        isDirty={false}
        isValidating={false}
      />
    </Stack>
  );
}

export function AccumulationAreasList() {
  const { tenantId } = useTenant();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get("facilityId");

  if (!tenantId) {
    return null;
  }

  if (!facilityId) {
    return <FacilityPickerPrompt tenantId={tenantId} />;
  }

  return <FacilityAccumulationAreas tenantId={tenantId} facilityId={facilityId} />;
}
